import { Link } from "react-router-dom";
import type {
  CatalogDraft,
  CatalogItem,
  Color,
  InvoicePreviewLine,
  ItemCategory,
} from "../../api/types";
import { DraftReviewRow, draftCanPromote, type DraftRowPatch } from "../catalog/DraftReviewRow";
import { LineItemPicker, LineZoneSelect } from "./PaperLinePickers";

export type LineLink = { itemId: string; zoneId: string };

/**
 * G-129 (OD-159) — the paper's lines that still have no item. The app
 * created an item for every line whose category the reader knew when the
 * paper was read; what is left here is a decision for a person: pick a
 * category and Create & link (the staging row, with the paper's photo, the
 * barcode picture and the suggestion), or pick an existing catalogue item.
 */
export function PaperOpenLinesTable({
  lines,
  links,
  categories,
  colors,
  items,
  zones,
  busyLineIds,
  lineErrors,
  createAllBusy,
  schedules,
  onPatchDraft,
  onPhotoDraft,
  onCreate,
  onCreateAll,
  onPickItem,
  onPickZone,
}: {
  lines: InvoicePreviewLine[];
  links: Record<string, LineLink>;
  categories: ItemCategory[];
  colors: Color[];
  items: CatalogItem[];
  zones: Array<{ id: string; name: string }>;
  busyLineIds: Set<string>;
  lineErrors: Record<string, string>;
  createAllBusy: boolean;
  /** Whether this paper becomes a delivery at all (a bill-only or items-only paper does not). */
  schedules: boolean;
  onPatchDraft: (draft: CatalogDraft, patch: DraftRowPatch) => Promise<void>;
  onPhotoDraft: (draftId: string, file: File | null) => Promise<void> | void;
  onCreate: (line: InvoicePreviewLine, draft: CatalogDraft) => Promise<void>;
  onCreateAll: (targets: Array<{ line: InvoicePreviewLine; draft: CatalogDraft }>) => Promise<void>;
  onPickItem: (lineId: string, itemId: string) => void;
  onPickZone: (lineId: string, zoneId: string) => void;
}) {
  const ready = lines.flatMap((line) =>
    line.draft && draftCanPromote(line.draft, categories) ? [{ line, draft: line.draft }] : [],
  );

  return (
    <div className="draft-review paper-open-lines">
      <div className="draft-review__toolbar">
        <p className="muted draft-review__note">
          {lines.length === 1 ? "1 line still needs an item" : `${lines.length} lines still need an item`}
          {" — "}
          {ready.length} of them {ready.length === 1 ? "has" : "have"} a category (chosen or suggested).{" "}
          {schedules
            ? "Scheduling waits until every line has one."
            : "Each becomes a catalogue item once it has one."}
        </p>
        <button
          type="button"
          className="button"
          disabled={createAllBusy || ready.length === 0}
          onClick={() => void onCreateAll(ready)}
        >
          {createAllBusy ? "Creating…" : `Create & link all with a category (${ready.length})`}
        </button>
      </div>
      <div className="table-scroll">
        <table className="draft-review__table">
          <thead>
            <tr>
              <th aria-label="Supplier photo" />
              <th>Line</th>
              <th>Name</th>
              <th>Category</th>
              <th>Colour</th>
              <th>Price once received</th>
              <th>Barcode</th>
              <th>Existing item instead</th>
              <th>Receiving zone</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const link = links[line.id] ?? { itemId: "", zoneId: "" };
              const busy = busyLineIds.has(line.id);
              const err = lineErrors[line.id];
              // The matcher's own sentence when it found candidates but would not
              // guess ("2 catalogue items are called exactly this — pick the right
              // one"); the old page showed it and G-129's first cut dropped it.
              const reason = line.matchHints.reason ?? null;
              const pickers = (
                <>
                  <td className="paper-line__pick">
                    <LineItemPicker
                      lineId={line.id}
                      itemId={link.itemId}
                      items={items}
                      onPick={(itemId) => onPickItem(line.id, itemId)}
                    />
                  </td>
                  <td className="paper-line__pick">
                    <LineZoneSelect
                      lineId={line.id}
                      zoneId={link.zoneId}
                      zones={zones}
                      onPick={(zoneId) => onPickZone(line.id, zoneId)}
                    />
                  </td>
                </>
              );
              if (!line.draft) {
                // No draft to create from: the same product is already
                // waiting in Catalog staging from another paper (it is staged
                // once), or its draft was deleted there. Only an existing
                // item can be picked here.
                return (
                  <tr key={line.id} className={busy ? "is-busy" : undefined}>
                    <td className="draft-review__thumb-cell" />
                    <td>
                      <code>{line.supplierSku ?? "—"}</code>
                      <div className="field-hint">#{line.lineIndex + 1}</div>
                    </td>
                    <td colSpan={5}>
                      <div>{line.descriptionRaw}</div>
                      {reason ? <div className="field-hint text-danger">{reason}</div> : null}
                      <div className="field-hint">
                        Not staged from this paper — the same product is already waiting in{" "}
                        <Link to="/app/items/drafts">Catalog staging</Link> from another paper, or its
                        draft was removed there. Pick an existing item.
                      </div>
                      {err ? <div className="text-danger">{err}</div> : null}
                    </td>
                    {pickers}
                    <td className="draft-review__actions" />
                  </tr>
                );
              }
              const draft = line.draft;
              const canCreate = draftCanPromote(draft, categories);
              return (
                <DraftReviewRow
                  key={line.id}
                  draft={draft}
                  categories={categories}
                  colors={colors}
                  busy={busy}
                  onPatch={onPatchDraft}
                  onPhoto={onPhotoDraft}
                  nameNote={reason}
                  extraCells={pickers}
                  actions={
                    <>
                      <button
                        type="button"
                        className="button"
                        disabled={busy || createAllBusy || !canCreate}
                        title={canCreate ? undefined : "Pick a category first"}
                        onClick={() => void onCreate(line, draft)}
                      >
                        {busy ? "Creating…" : "Create & link"}
                      </button>
                      <div className="field-hint">#{line.lineIndex + 1}</div>
                      {err ? <div className="text-danger">{err}</div> : null}
                    </>
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
